import * as React from 'react'
import { FormField } from '@/components/templates/entity-form'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

export interface EditableDonation {
  id: number
  gift_on: string
  amount: string
  campaign_id?: number | null
  tribute_type?: 'honor' | 'memory' | null
  honoree_name?: string | null
  display_anonymous?: boolean
  donor_comment?: string | null
  staff_note?: string | null
}

export interface EditableDonationFacts {
  campaign_id: number | null
  tribute_type: 'honor' | 'memory' | null
  honoree_name: string | null
  display_anonymous: boolean
  donor_comment: string | null
  staff_note: string | null
}

interface Campaign {
  id: number
  name: string
}

export function EditDonationDialog({
  donation,
  campaigns,
  campaignsLoading,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  donation: EditableDonation
  campaigns: Campaign[]
  campaignsLoading: boolean
  pending: boolean
  error: Error | null
  onClose: () => void
  onSubmit: (facts: EditableDonationFacts) => void
}) {
  const [facts, setFacts] = React.useState<EditableDonationFacts>(() => ({
    campaign_id: donation.campaign_id ?? null,
    tribute_type: donation.tribute_type ?? null,
    honoree_name: donation.honoree_name ?? null,
    display_anonymous: donation.display_anonymous ?? false,
    donor_comment: donation.donor_comment ?? null,
    staff_note: donation.staff_note ?? null,
  }))
  const set = (change: Partial<EditableDonationFacts>) =>
    setFacts((current) => ({ ...current, ...change }))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit(facts)
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit donation facts</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4 sm:grid-cols-2">
            <FormField label="Amount" htmlFor="edit-donation-amount">
              <Input id="edit-donation-amount" className="h-8" value={donation.amount} disabled />
            </FormField>
            <FormField label="Date received" htmlFor="edit-donation-date">
              <Input id="edit-donation-date" className="h-8" value={donation.gift_on} disabled />
            </FormField>
            <FormField label="Campaign">
              <Select
                value={facts.campaign_id ? String(facts.campaign_id) : 'general'}
                onValueChange={(value) =>
                  set({ campaign_id: value === 'general' ? null : Number(value) })
                }
                disabled={campaignsLoading}
              >
                <SelectTrigger aria-label="Campaign" className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={String(campaign.id)}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <DonationOptionalFields facts={facts} setFacts={set} />
          </div>
          {error && <p className="text-body-sm text-destructive">Unable to save donation facts.</p>}
          <DialogFooter>
            <Button type="button" variant="outline" className="h-8" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="h-8" disabled={pending}>
              {pending ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DonationOptionalFields({
  facts,
  setFacts,
}: {
  facts: EditableDonationFacts
  setFacts: (change: Partial<EditableDonationFacts>) => void
}) {
  return (
    <>
      {facts.tribute_type ? (
        <>
          <FormField label="Tribute type">
            <Select
              value={facts.tribute_type}
              onValueChange={(value: 'honor' | 'memory') => setFacts({ tribute_type: value })}
            >
              <SelectTrigger aria-label="Tribute type" className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="honor">In honor of</SelectItem>
                <SelectItem value="memory">In memory of</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Honoree name" htmlFor="edit-donation-honoree">
            <Input
              id="edit-donation-honoree"
              className="h-8"
              value={facts.honoree_name ?? ''}
              onChange={(event) => setFacts({ honoree_name: event.target.value || null })}
            />
          </FormField>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="justify-self-start text-caption"
            onClick={() => setFacts({ tribute_type: null, honoree_name: null })}
          >
            Clear tribute
          </Button>
        </>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="justify-self-start border-dashed"
          onClick={() => setFacts({ tribute_type: 'honor' })}
        >
          + Add tribute
        </Button>
      )}
      <div className="flex items-center gap-2 text-body-sm sm:col-span-2">
        <Checkbox
          id="edit-donation-anonymous"
          checked={facts.display_anonymous}
          onCheckedChange={(checked) => setFacts({ display_anonymous: checked === true })}
        />
        <label htmlFor="edit-donation-anonymous">Display as Anonymous</label>
      </div>
      <FormField label="Donor comment" htmlFor="edit-donation-comment">
        <Textarea
          id="edit-donation-comment"
          className="min-h-16"
          value={facts.donor_comment ?? ''}
          onChange={(event) => setFacts({ donor_comment: event.target.value || null })}
        />
      </FormField>
      <FormField label="Staff note" htmlFor="edit-donation-note">
        <Textarea
          id="edit-donation-note"
          className="min-h-16"
          value={facts.staff_note ?? ''}
          onChange={(event) => setFacts({ staff_note: event.target.value || null })}
        />
      </FormField>
    </>
  )
}
